import React, { useState, useEffect, useMemo } from 'react';
import { toast, toastError, toastSuccess } from '../../utils/toast';
import ReactDOM from 'react-dom';
import { useUser } from '@clerk/clerk-react';
import { format, addDays, subDays, startOfWeek, endOfWeek } from 'date-fns';
import axios from 'axios';
import { getToken, getApiUrl, getRelativeDateText } from './utils';
import { buildWorkoutDateList, findNextWorkoutDate as findNext, findPrevWorkoutDate as findPrev } from './navigationHelpers';
import { ChatManagerContext } from '../ChatManager';
import WorkoutNavigationHeader from './WorkoutNavigationHeader';
import { HelpModeProvider, useHelpMode, ContentParser, HelpModeToggle } from '../HelpMode';
import '../HelpMode/styles.css';
import { debugLog, debugWarn, debugError } from '../../utils/debug';
import { copyToClipboard as copyText } from '../ChatInterface/formatUtils';

// Component to render minimized chats within the workout modal
const MinimizedChatsInWorkout = () => {
  const context = React.useContext(ChatManagerContext);
  // Gracefully handle missing provider (e.g. portal race condition on double-tap)
  if (!context) return null;
  const { chats, maximizeChat, closeChat } = context;
  const minimizedChats = chats.filter(chat => chat.isMinimized);

  if (minimizedChats.length === 0) return null;

  const getTruncatedInstruction = (instruction) => {
    if (!instruction) return 'Workout Help';
    const cleanText = instruction.replace(/\*\*(.*?)\*\*/g, '$1').replace(/[#*_`]/g, '');
    return cleanText.length > 40 ? cleanText.substring(0, 40) + '...' : cleanText;
  };

  const getChatTitle = (instruction) => {
    if (!instruction) return 'AI Coach';
    const cleanText = instruction.replace(/\*\*(.*?)\*\*/g, '$1').replace(/[#*_`]/g, '');
    
    if (cleanText.toLowerCase().includes('breathing')) return 'Breathing Technique';
    if (cleanText.toLowerCase().includes('pace') || cleanText.toLowerCase().includes('speed')) return 'Pacing Guide';
    if (cleanText.toLowerCase().includes('form') || cleanText.toLowerCase().includes('technique')) return 'Form Tips';
    if (cleanText.toLowerCase().includes('recovery') || cleanText.toLowerCase().includes('rest')) return 'Recovery Advice';
    if (cleanText.toLowerCase().includes('warm') || cleanText.toLowerCase().includes('cool')) return 'Warm-up/Cool-down';
    if (cleanText.toLowerCase().includes('terrain') || cleanText.toLowerCase().includes('surface')) return 'Terrain Tips';
    if (cleanText.toLowerCase().includes('hydration') || cleanText.toLowerCase().includes('water')) return 'Hydration Guide';
    if (cleanText.toLowerCase().includes('negative split')) return 'Pacing Strategy';
    if (cleanText.toLowerCase().includes('cadence') || cleanText.toLowerCase().includes('steps')) return 'Cadence Tips';
    
    const words = cleanText.split(' ').slice(0, 3);
    return words.join(' ') + (cleanText.split(' ').length > 3 ? '...' : '');
  };

  return (
    <div className="absolute bottom-4 left-4 right-4 z-50 pointer-events-none">
      <div className="flex flex-col space-y-2 max-w-sm">
        {minimizedChats.map((chat, index) => (
          <div 
            key={chat.id}
            className="bg-gray-800/90 backdrop-blur-sm border border-gray-600 rounded-lg p-3 shadow-lg pointer-events-auto cursor-pointer hover:bg-gray-700/90 transition-all duration-200 active:scale-95"
            onClick={() => {
              debugLog('Minimized chat in workout clicked to maximize:', chat.id);
              maximizeChat(chat.id);
            }}
            style={{
              transform: `translateY(${index * 2}px)`,
              zIndex: 1000 - index
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                  <span className="text-xs font-medium text-blue-300 truncate">
                    {getChatTitle(chat.explanationData?.instruction)}
                  </span>
                </div>
                <p className="text-xs text-gray-300 mt-1 truncate">
                  {getTruncatedInstruction(chat.explanationData?.instruction)}
                </p>
              </div>
              <div className="flex items-center space-x-1 ml-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    debugLog('Maximize button clicked in workout for chat:', chat.id);
                    maximizeChat(chat.id);
                  }}
                  className="p-1 hover:bg-gray-600 rounded transition-colors"
                  title="Maximize" aria-label="Maximize chat"
                >
                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    closeChat(chat.id);
                  }}
                  className="p-1 hover:bg-red-600/20 rounded transition-colors"
                  title="Close" aria-label="Close chat"
                >
                  <svg className="w-3 h-3 text-gray-400 hover:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RegimenDayModalContent = ({ isOpen, onClose, run, type, onSuccess, regimens = [], onWorkoutChange }) => {
  const { user } = useUser();
  const [currentWorkoutDate, setCurrentWorkoutDate] = useState(run?.date || new Date());
  const [currentWorkout, setCurrentWorkout] = useState(run);
  
  // Debug: Log when modal receives props
  useEffect(() => {
    // Debug logging removed
  }, [isOpen, run, regimens.length]);
  const [formData, setFormData] = useState({
    status: run?.status || 'planned',
    completion_details: run?.completion_details || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isEditingFeedback, setIsEditingFeedback] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [isRegimenReady, setIsRegimenReady] = useState(false);

  // Use Help Mode context instead of local state
  const { isHelpMode } = useHelpMode();

  // Ref to workout details section for copying
  const workoutDetailsRef = React.useRef(null);

  // Function to extract formatted text from DOM element
  const getFormattedTextFromElement = (element) => {
    if (!element) return '';
    
    let text = '';
    const traverse = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName.toLowerCase();
        
        // Add line breaks for block elements
        if (['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li'].includes(tag)) {
          if (text && !text.endsWith('\n')) {
            text += '\n';
          }
        }
        
        // Process children
        Array.from(node.childNodes).forEach(child => traverse(child));
        
        // Add line breaks after block elements
        if (['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol'].includes(tag)) {
          if (!text.endsWith('\n')) {
            text += '\n';
          }
        }
      }
    };
    
    traverse(element);
    return text.trim();
  };

  // Use the shared copyToClipboard util — wrapped to keep the existing sync API
  const copyToClipboardFallback = (text) => {
    // Best-effort sync: kick off the async copy and return true (callers don't await).
    copyText(text);
    return true;
  };

  // Toggle a feedback chip into/out of completion_details
  const toggleChip = (chipText) => {
    setFormData(prev => {
      const cur = prev.completion_details || '';
      const has = cur.includes(chipText);
      return {
        ...prev,
        completion_details: has
          ? cur.replace(chipText, '').replace(/\s{2,}/g, ' ').trim()
          : (cur ? `${cur} ${chipText}` : chipText).trim(),
      };
    });
  };

  const isChipActive = (chipText) =>
    Boolean((formData.completion_details || '').includes(chipText));

  // Helper functions for navigation
  const getRegimenForDate = (date) => {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1; // Monday = 0, Sunday = 6
    
    const weekStartDateString = format(weekStart, 'yyyy-MM-dd');
    
    const regimen = regimens.find(r => r.start_of_week_date === weekStartDateString);
    
    if (!regimen) {
      // Don't log warning here - useEffect handles logging after wait period
      return null;
    }
    
    const title = regimen.regimen_titles[dayIndex];
    if (!title) {
      // This is expected - not every day has a workout scheduled (rest days)
      return null;
    }
    
    const workout = {
      id: `${regimen.id}-${dayIndex}`,
      regimen_id: regimen.id,
      day_index: dayIndex,
      title,
      subtitle: regimen.regimen_subtitles[dayIndex],
      details: regimen.regimen_details[dayIndex],
      status: regimen.completion_status[dayIndex] || 'planned',
      completion_details: regimen.completion_details[dayIndex] || '',
      enhancement_status: regimen.enhancement_status ? regimen.enhancement_status[dayIndex] : null,
      date: date,
      distance_km: regimen.day_distances_km?.[dayIndex] ?? null,
      week_planned_volume_km: regimen.planned_volume_km ?? null
    };
    
    return workout;
  };

  // Wait for regimen to be available before rendering content (fixes race condition)
  useEffect(() => {
    const regimen = getRegimenForDate(currentWorkoutDate);
    if (regimen) {
      setIsRegimenReady(true);
    } else {
      // Reset ready state when date changes and regimen not found yet
      setIsRegimenReady(false);
      // Only log warning once after we've waited a bit
      const timer = setTimeout(() => {
        const stillMissing = !getRegimenForDate(currentWorkoutDate);
        if (stillMissing) {
          const weekStart = startOfWeek(currentWorkoutDate, { weekStartsOn: 1 });
          const weekStartDateString = format(weekStart, 'yyyy-MM-dd');
          debugWarn('[RegimenDayModal] No regimen found for week:', weekStartDateString);
        }
      }, 500); // Wait 500ms before logging warning
      return () => clearTimeout(timer);
    }
  }, [currentWorkoutDate, regimens]);

  // Compute load meta for the per-workout load bar
  // Shows workout's share of planned weekly volume (factual only, no subjective labels)
  const computeDayLoadMeta = (distanceKm, weekPlannedVolumeKm) => {
    if (!distanceKm || !weekPlannedVolumeKm || weekPlannedVolumeKm <= 0) {
      return {
        ratioOfWeek: 0,
        bucket: "low",
        filledBlocks: 0
      };
    }

    const ratio = distanceKm / weekPlannedVolumeKm;

    // Internal bucket for color intensity only (not shown to user)
    let bucket;
    if (ratio < 0.15) bucket = "low";
    else if (ratio < 0.25) bucket = "medium";
    else bucket = "high";

    const maxBlocks = 6;
    let filledBlocks = Math.round(ratio * maxBlocks);
    if (filledBlocks < 0) filledBlocks = 0;
    if (filledBlocks > maxBlocks) filledBlocks = maxBlocks;

    return { ratioOfWeek: ratio, bucket, filledBlocks };
  };

  const loadMeta = computeDayLoadMeta(currentWorkout?.distance_km, currentWorkout?.week_planned_volume_km);

  // Build sorted list of all workout dates from regimens (Bug #14B fix)
  // This allows navigation to workouts regardless of how far apart they are
  const allWorkoutDates = useMemo(() => buildWorkoutDateList(regimens), [regimens]);

  const findNextWorkoutDate = (currentDate) => findNext(allWorkoutDates, currentDate);
  const findPrevWorkoutDate = (currentDate) => findPrev(allWorkoutDates, currentDate);

  // Navigation handlers
  const handlePrevWorkout = () => {
    const prevDate = findPrevWorkoutDate(currentWorkoutDate);
    if (prevDate) {
      const workout = getRegimenForDate(prevDate);
      if (workout) {
        setCurrentWorkoutDate(prevDate);
        setCurrentWorkout(workout);
        setFormData({
          status: workout.status || 'planned',
          completion_details: workout.completion_details || ''
        });
        if (onWorkoutChange) {
          onWorkoutChange(workout);
        }
      }
    }
  };

  const handleNextWorkout = () => {
    const nextDate = findNextWorkoutDate(currentWorkoutDate);
    if (nextDate) {
      const workout = getRegimenForDate(nextDate);
      if (workout) {
        setCurrentWorkoutDate(nextDate);
        setCurrentWorkout(workout);
        setFormData({
          status: workout.status || 'planned',
          completion_details: workout.completion_details || ''
        });
        if (onWorkoutChange) {
          onWorkoutChange(workout);
        }
      }
    }
  };

  const handleNavigateToToday = () => {
    const today = new Date();
    const workout = getRegimenForDate(today);
    if (workout) {
      setCurrentWorkoutDate(today);
      setCurrentWorkout(workout);
      setFormData({
        status: workout.status || 'planned',
        completion_details: workout.completion_details || ''
      });
      if (onWorkoutChange) {
        onWorkoutChange(workout);
      }
    }
  };

  // Check if today has a workout
  const hasTodayWorkout = () => {
    const today = new Date();
    return !!getRegimenForDate(today);
  };

  // Keyboard navigation support
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      // Only handle arrow keys if not typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevWorkout();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextWorkout();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentWorkoutDate, regimens]); // Re-attach when workout changes

  // Update current workout when run prop changes
  useEffect(() => {
    if (run) {
      setCurrentWorkout(run);
      setCurrentWorkoutDate(run.date);
      setIsEditingFeedback(false); // Reset editing state when workout changes
      setShowFeedbackForm(false); // Reset feedback form visibility when workout changes
      
      setFormData({
        status: run.status || 'planned',
        completion_details: run.completion_details || ''
      });
    }
  }, [run]);
  
  // Debug: Log when regimens prop updates
  useEffect(() => {
    // Debug logging removed
  }, [regimens, isOpen, currentWorkoutDate, currentWorkout]);

  // Determine if workout details are enhanced vs basic (trust backend designation)
  const isDetailsEnhanced = (details, enhancementStatus) => {
    if (!details) return false;
    
    // Trust the backend's enhancement status if available
    // Both "enhanced" and "updated" indicate enhanced content
    if (enhancementStatus === "enhanced" || enhancementStatus === "updated") {
      return true;
    }
    if (enhancementStatus === "core") {
      return false;
    }
    
    // Fallback: if enhancement status is not available, use content length as indicator
    // This is a temporary fallback for existing data
    const detailsLength = details.length;
    return detailsLength >= 1000; // Simple fallback
  };

  // Build workout context for Help Mode
  const workoutContext = {
    workoutId: currentWorkout?.regimen_id,
    workoutContext: currentWorkout?.details,
    title: format(currentWorkoutDate, 'EEEE, MMMM d'),
    subtitle: getRelativeDateText(format(currentWorkoutDate, "yyyy-MM-dd'T'HH:mm"))
  };

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    setError('');

    try {
      const token = await getToken(user);
      
      // Get the day name for this regimen
      const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const dayName = dayNames[run.day_index];
      
      const updateData = {
        completion_status: {
          [dayName]: newStatus
        }
      };

      const API_URL = getApiUrl(); // Get at runtime
      await axios.put(`${API_URL}/api/regimens/weekly/${currentWorkout?.regimen_id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onSuccess();
      onClose();
    } catch (error) {
      debugError('Error updating status:', error);
      setError('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const handleCompletionSubmit = async (e) => {
    e.preventDefault();
    
    // Allow both completed and skipped statuses
    if (formData.status !== 'completed' && formData.status !== 'skipped') return;
    
    setLoading(true);
    setError('');

    try {
      const token = await getToken(user);
      
      // Get the day name for this regimen
      const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const dayName = dayNames[run.day_index];
      
      // Default message based on status if no details provided
      const defaultMessage = formData.status === 'completed' 
        ? `Completed on ${format(new Date(), 'MMM d, yyyy \'at\' h:mm a')}`
        : `Skipped on ${format(new Date(), 'MMM d, yyyy \'at\' h:mm a')}`;
      
      // Save both status AND details
      const updateData = {
        completion_status: {
          [dayName]: formData.status
        },
        completion_details: {
          [dayName]: formData.completion_details.trim() || defaultMessage
        }
      };

      const API_URL = getApiUrl(); // Get at runtime
      await axios.put(`${API_URL}/api/regimens/weekly/${currentWorkout?.regimen_id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsEditingFeedback(false);
      setShowFeedbackForm(false);
      onSuccess();
      onClose();
    } catch (error) {
      debugError('Error updating completion details:', error);
      // Handle different error formats
      let errorMessage = 'Failed to update details';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Handle validation errors from FastAPI
          errorMessage = error.response.data.detail.map(err => err.msg || err).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetDeleteState = () => {
    setConfirmDelete(false);
    setIsDeleting(false);
    setDeleteError('');
  };

  const closeMoreMenu = () => {
    setShowMoreMenu(false);
    resetDeleteState();
  };

  const handleDeleteWorkout = async () => {
    if (!run || typeof run.day_index !== 'number' || !currentWorkout?.regimen_id) {
      setDeleteError('Workout information is missing. Please close and reopen the day.');
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteError('');

      const token = await getToken(user);
      const API_URL = getApiUrl();
      const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const dayName = dayNames[run.day_index];

      await axios.put(
        `${API_URL}/api/regimens/weekly/${currentWorkout.regimen_id}`,
        { delete_days: [dayName] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toastSuccess('Workout removed.');

      closeMoreMenu();
      onSuccess();
      onClose();
    } catch (error) {
      debugError('Error deleting workout:', error);

      const errorMessage =
        error?.response?.data?.detail
          ? Array.isArray(error.response.data.detail)
            ? error.response.data.detail.map((detail) => detail.msg || detail).join(', ')
            : typeof error.response.data.detail === 'string'
              ? error.response.data.detail
              : 'Failed to delete workout.'
          : error?.message || 'Failed to delete workout.';

      setDeleteError(errorMessage);
      setIsDeleting(false);
    }
  };

  const handleShareModal = async () => {
    // Validate required data
    if (!currentWorkout || !currentWorkoutDate) {
      debugError('Share error: Missing workout data', { currentWorkout, currentWorkoutDate });
      toastError('Workout information not available. Please try again.');
      return;
    }

    // Validate date is valid
    if (!(currentWorkoutDate instanceof Date) || isNaN(currentWorkoutDate.getTime())) {
      debugError('Share error: Invalid date', currentWorkoutDate);
      toastError('Invalid date information. Please try again.');
      return;
    }

    // Build share text
    let shareText;
    try {
      const dateStr = format(currentWorkoutDate, 'EEEE, MMMM d');
      const title = currentWorkout.title || 'Workout';
      const subtitle = currentWorkout.subtitle ? `\n${currentWorkout.subtitle}` : '';
      const status = currentWorkout.status === 'completed' ? '\n✅ Completed' 
                   : currentWorkout.status === 'skipped' ? '\n⏭️ Skipped' 
                   : '\n📅 Planned';
      shareText = `${dateStr} - ${title}${subtitle}${status}`;
    } catch (formatError) {
      debugError('Error formatting date:', formatError);
      toastError('Error preparing share content. Please try again.');
      return;
    }

    // Validate share text is not empty
    if (!shareText || shareText.trim() === '') {
      debugError('Share error: Empty share text');
      toastError('No workout information to share.');
      return;
    }

    try {
      // Try native share API first (mobile/desktop with share support)
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Training Session',
            text: shareText,
          });
          // Share succeeded, return early
          return;
        } catch (shareError) {
          // User cancelled share dialog - don't show error
          if (shareError.name === 'AbortError') {
            return; // User cancelled, silently exit
          }
          // Other share errors - fall through to clipboard
          debugWarn('Native share failed, trying clipboard:', shareError);
        }
      }

      // Try modern clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(shareText);
          toastSuccess('Workout details copied to clipboard!');
          return;
        } catch (clipboardError) {
          debugWarn('Clipboard API failed, trying fallback:', clipboardError);
          // Fall through to execCommand fallback
        }
      }

      // Final fallback: use execCommand
      copyText(shareText);
      const success = true;
      if (success) {
        toastSuccess('Workout details copied to clipboard!');
      } else {
        toastError('Could not copy to clipboard. Please try again.');
      }

    } catch (error) {
      debugError('Unexpected error in share flow:', error);
      // Last resort: use execCommand fallback
      copyText(shareText);
      const success = true;
      if (success) {
        toastSuccess('Workout details copied to clipboard!');
      }
    }
  };

  if (!isOpen) return null;

  // Show loading skeleton while waiting for regimen data
  if (!isRegimenReady) {
    return ReactDOM.createPortal(
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-[9999] p-2 sm:p-4 animate-fade-in"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div 
          data-modal="training-session"
          className="bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-xl rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-xl border border-gray-700/50 animate-float-up flex flex-col relative"
        >
          <div className="px-4 pt-4 pb-2">
            <div className="h-10 bg-gray-700/50 rounded-lg animate-pulse"></div>
          </div>
          <div className="px-4 py-6 space-y-4">
            <div className="h-8 bg-gray-700/50 rounded-lg animate-pulse w-3/4"></div>
            <div className="h-4 bg-gray-700/50 rounded-lg animate-pulse w-1/2"></div>
            <div className="h-32 bg-gray-700/50 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-[9999] p-2 sm:p-4 animate-fade-in"
      onClick={(e) => {
        // Only close if clicking on the backdrop itself, not on the modal content
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        data-modal="training-session"
        className="bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-xl rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-xl border border-gray-700/50 animate-float-up flex flex-col relative"
      >

        {/* Navigation Header */}
        <div className="px-4 pt-4">
          <WorkoutNavigationHeader
            currentDate={currentWorkoutDate}
            onPrevWorkout={handlePrevWorkout}
            onNextWorkout={handleNextWorkout}
            onNavigateToToday={handleNavigateToToday}
            hasPrevWorkout={!!findPrevWorkoutDate(currentWorkoutDate)}
            hasNextWorkout={!!findNextWorkoutDate(currentWorkoutDate)}
            hasTodayWorkout={hasTodayWorkout()}
          />
        </div>

        {/* Stunning Header */}
        <div className="relative bg-gradient-to-r from-blue-500/25 via-blue-400/20 to-blue-500/25 border-b border-gray-600/30">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent"></div>
          <div className="relative px-4 py-4 sm:px-5 sm:py-5">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-400/25 rounded-lg border border-blue-300/40 backdrop-blur-sm">
                  <svg className="w-5 h-5 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg sm:text-xl font-bold text-white leading-tight">
                    {currentWorkout?.title || 'Workout'}
                  </h3>
                  <p className="text-sm text-blue-100/90">{currentWorkout?.subtitle || ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleShareModal}
                  className="group p-2 hover:bg-blue-400/25 rounded-lg transition-all duration-200 border border-gray-600/30 hover:border-blue-300/60"
                  title="Share this training session"
                >
                  <svg className="w-4 h-4 text-gray-300 group-hover:text-blue-200 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                </button>
                <HelpModeToggle />
                <button
                  onClick={onClose}
                  className="group p-2 hover:bg-red-400/25 rounded-lg transition-all duration-200 border border-gray-600/30 hover:border-red-300/60"
                >
                  <svg className="w-4 h-4 text-gray-300 group-hover:text-red-200 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Help Mode Banner */}
        {isHelpMode && (
          <div className="bg-gradient-to-r from-yellow-500/90 to-orange-500/90 border-b border-yellow-300/50">
            <div className="px-4 py-3 sm:px-6">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                <div>
                  <h3 className="text-white font-semibold text-sm">Help Mode Active</h3>
                  <p className="text-white/90 text-xs">Tap any highlighted phrase for an explanation</p>
                </div>
              </div>
                  </div>
                    </div>
                  )}

        {/* Load Bar - Per-workout volume indicator (factual only) */}
        {currentWorkout?.distance_km && currentWorkout?.week_planned_volume_km && (
          <div className="px-4 py-3 sm:px-6 border-b border-gray-700/50 bg-gray-800/30">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1" title="Workout load (share of weekly plan)">
                {Array.from({ length: 6 }).map((_, i) => (
                  <span
                    key={i}
                    className={`w-3 h-3 rounded-sm transition-colors ${
                      i < loadMeta.filledBlocks
                        ? loadMeta.bucket === 'high'
                          ? 'bg-blue-400'
                          : loadMeta.bucket === 'medium'
                          ? 'bg-blue-400'
                          : 'bg-blue-400'
                        : 'bg-gray-600/50'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-300">
                {currentWorkout.distance_km.toFixed(1)} km
                <span className="text-gray-500 mx-1">·</span>
                {Math.round(loadMeta.ratioOfWeek * 100)}% of weekly planned volume
              </span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-4">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

            {/* Status Section - Single-Session UI */}
            <div className="space-y-3">
                {/* Clickable Status Badge + More Options on same line */}
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (formData.status === 'planned' || formData.status === 'pending') {
                        // When user clicks "Scheduled", mark as completed and show feedback form
                        setFormData(prev => ({ ...prev, status: 'completed' }));
                        setShowFeedbackForm(true);
                      } else if (formData.status === 'completed' && !currentWorkout?.completion_details) {
                        // If completed but no feedback, toggle back to scheduled if no text
                        if (!formData.completion_details || formData.completion_details.trim() === '') {
                          setFormData(prev => ({ ...prev, status: 'planned' }));
                          setShowFeedbackForm(false);
                        } else {
                          // Has text, just toggle form visibility
                          setShowFeedbackForm(!showFeedbackForm);
                        }
                      }
                    }}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors active:scale-95 ${
                      formData.status === 'completed'
                        ? 'bg-green-500/15 text-green-300 ring-1 ring-green-500/30 cursor-pointer hover:bg-green-500/20'
                        : formData.status === 'skipped'
                        ? 'bg-orange-500/15 text-orange-300 ring-1 ring-orange-500/30'
                        : 'bg-blue-500/15 text-blue-300 ring-1 ring-blue-500/30 cursor-pointer hover:bg-blue-500/20'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                    <span>{formData.status === 'completed' ? 'Completed' : formData.status === 'skipped' ? 'Skipped' : 'Scheduled'}</span>
                  </button>
                  
                  {/* More Options Button - Now on same line, right-aligned */}
                  <button
                    type="button"
                    onClick={() =>
                      setShowMoreMenu((prev) => {
                        const next = !prev;
                        if (!next) {
                          resetDeleteState();
                        }
                        return next;
                      })
                    }
                    className="px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-600/40 border border-gray-600/40 transition-all duration-200"
                    title="More Options"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                  </button>
                </div>
                
                {/* More Options Menu */}
                {showMoreMenu && (
                  <div className="bg-gradient-to-br from-gray-700/50 to-gray-800/50 border border-gray-600/40 rounded-lg overflow-hidden backdrop-blur-sm">
                    <button
                      type="button"
                      onClick={async () => {
                        setLoading(true);
                        try {
                          const token = await getToken(user);
                          const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                          const dayName = dayNames[run.day_index];
                          const API_URL = getApiUrl(); // Get at runtime
                          
                          await axios.put(`${API_URL}/api/regimens/weekly/${currentWorkout?.regimen_id}`, {
                            completion_status: { [dayName]: 'planned' },
                            completion_details: { [dayName]: '' }
                          }, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          
                          setFormData(prev => ({ ...prev, status: 'planned', completion_details: '' }));
                          setShowFeedbackForm(false);
                          setIsEditingFeedback(false);
                          onSuccess(); // Refresh data
                          // Don't call onClose() - keep modal open
                        } catch (error) {
                          debugError('Error resetting status:', error);
                          setError('Failed to reset status');
                        } finally {
                          setLoading(false);
                        }
                        closeMoreMenu();
                      }}
                      disabled={loading || isDeleting}
                      className="w-full px-4 py-3 text-left text-sm text-gray-200 hover:text-white hover:bg-gray-600/40 transition-colors flex items-center gap-3 border-b border-gray-600/30"
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                      <span>Reset to Scheduled</span>
                    </button>
                    
                    {/* Mark as Skipped option */}
                    {formData.status !== 'skipped' && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, status: 'skipped' }));
                          setShowFeedbackForm(true);
                          closeMoreMenu();
                        }}
                        disabled={loading || isDeleting}
                        className="w-full px-4 py-3 text-left text-sm text-gray-200 hover:text-white hover:bg-gray-600/40 transition-colors flex items-center gap-3 border-b border-gray-600/30"
                      >
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Mark as Skipped</span>
                      </button>
                    )}
                    
                    <div className="border-b border-gray-600/30">
                      {!confirmDelete ? (
                        <button
                          type="button"
                          onClick={() => {
                            setConfirmDelete(true);
                            setDeleteError('');
                          }}
                          disabled={loading || isDeleting}
                          className="w-full px-4 py-3 text-left text-sm text-red-300 hover:text-red-100 hover:bg-red-900/20 transition-colors flex items-center gap-3"
                        >
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span>Delete Workout</span>
                        </button>
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-200 space-y-3 bg-red-900/10">
                          <p className="font-medium text-red-200">Delete this workout?</p>
                          <p className="text-xs text-red-200/80">
                            This removes the session from your plan. You can’t undo this action.
                          </p>
                          {deleteError ? (
                            <p className="text-xs text-red-300">{deleteError}</p>
                          ) : null}
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={handleDeleteWorkout}
                              disabled={isDeleting}
                              className="flex-1 px-3 py-2 text-xs font-semibold rounded-md bg-red-600/80 hover:bg-red-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isDeleting ? 'Deleting…' : 'Yes, delete'}
                            </button>
                            <button
                              type="button"
                              onClick={() => resetDeleteState()}
                              disabled={isDeleting}
                              className="flex-1 px-3 py-2 text-xs font-semibold rounded-md border border-gray-600/60 text-gray-200 hover:text-white hover:bg-gray-700/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <button
                      type="button"
                      onClick={() => {
                        // Export to calendar logic - to be implemented
                        toast('Export to calendar — coming soon');
                        closeMoreMenu();
                      }}
                      disabled={loading || isDeleting}
                      className="w-full px-4 py-3 text-left text-sm text-gray-200 hover:text-white hover:bg-gray-600/40 transition-colors flex items-center gap-3 border-b border-gray-600/30"
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Export to Calendar</span>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => {
                        // Extract formatted text directly from the rendered DOM
                        let workoutText = `${currentWorkout.title}\n`;
                        if (currentWorkout.subtitle) {
                          workoutText += `${currentWorkout.subtitle}\n`;
                        }
                        workoutText += '\n';
                        
                        // Get the formatted content from the DOM
                        if (workoutDetailsRef.current) {
                          workoutText += getFormattedTextFromElement(workoutDetailsRef.current);
                        } else {
                          // Fallback if ref not available
                          workoutText += currentWorkout.details || '';
                        }
                        
                        // Add branding footer
                        workoutText += '\n\n---\nCreated with Runium.ai - Your AI Running Coach';
                        
                        // Try modern clipboard API first
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          navigator.clipboard.writeText(workoutText).then(() => {
                            toastSuccess('Workout details copied to clipboard!');
                          }).catch(err => {
                            debugError('Clipboard API failed:', err);
                            copyText(workoutText);
                            const success = true;
                            if (success) {
                              toastSuccess('Workout details copied to clipboard!');
                            } else {
                              toastError('Could not copy to clipboard. Please try again.');
                            }
                          });
                        } else {
                          // Fallback for older browsers or non-HTTPS
                          copyText(workoutText);
                          const success = true;
                          if (success) {
                            toastSuccess('Workout details copied to clipboard!');
                          } else {
                            toastError('Could not copy to clipboard. Please try again.');
                          }
                        }
                        
                        closeMoreMenu();
                      }}
                      disabled={loading || isDeleting}
                      className="w-full px-4 py-3 text-left text-sm text-gray-200 hover:text-white hover:bg-gray-600/40 transition-colors flex items-center gap-3"
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>Copy to Clipboard</span>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => {
                        toast('Share as image — coming soon');
                        closeMoreMenu();
                      }}
                      disabled={loading || isDeleting}
                      className="w-full px-4 py-3 text-left text-sm text-gray-200 hover:text-white hover:bg-gray-600/40 transition-colors flex items-center gap-3"
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                      </svg>
                      <span>Share as Image</span>
                    </button>
              </div>
                )}
            </div>

          {/* Show existing feedback for completed workouts */}
          {!isEditingFeedback && (formData.status === 'completed' || formData.status === 'skipped') && currentWorkout?.completion_details && currentWorkout.completion_details.trim() && (
            <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 border border-green-500/40 rounded-lg p-4 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-green-200 mb-1">
                    {formData.status === 'completed' ? 'Workout Notes' : 'Skip Reason'}
                  </h4>
                  <p className="text-sm text-gray-200 leading-relaxed">{currentWorkout.completion_details}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingFeedback(true);
                    setShowFeedbackForm(true);
                  }}
                  className="flex-shrink-0 text-xs text-green-300 hover:text-green-200 transition-colors"
                >
                  Edit
                </button>
              </div>
            </div>
          )}

          {/* Completion Details - Show if form is visible OR editing OR (completed without feedback) */}
            {(formData.status === 'completed' || formData.status === 'skipped') && (showFeedbackForm || isEditingFeedback || !currentWorkout?.completion_details || !currentWorkout.completion_details.trim()) && (
            <form onSubmit={handleCompletionSubmit} className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-200 uppercase tracking-wide">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                {formData.status === 'completed' ? 'How did it go?' : 'Why did you skip?'} <span className="text-gray-400 text-xs normal-case">(Optional)</span>
                </label>
              
              {/* Quick Response Chips */}
              <div className="bg-gray-700/40 border border-gray-600/50 rounded-lg p-3 backdrop-blur-sm">
                <p className="text-xs text-gray-300 mb-2">Quick responses:</p>
                <div className="flex flex-wrap gap-2">
                  {formData.status === 'completed' ? (
                    <>
                      <button type="button" onClick={() => toggleChip('😊 Felt great!')} className={`px-3 py-2 text-xs rounded-lg border transition-colors active:scale-95 ${isChipActive('😊 Felt great!') ? 'bg-blue-500/20 text-blue-300 border-blue-400/40' : 'bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 border-gray-600/40'}`}>😊 Felt great!</button>
                      <button type="button" onClick={() => toggleChip('😐 It was okay')} className={`px-3 py-2 text-xs rounded-lg border transition-colors active:scale-95 ${isChipActive('😐 It was okay') ? 'bg-blue-500/20 text-blue-300 border-blue-400/40' : 'bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 border-gray-600/40'}`}>😐 It was okay</button>
                      <button type="button" onClick={() => toggleChip('😓 Tough but finished')} className={`px-3 py-2 text-xs rounded-lg border transition-colors active:scale-95 ${isChipActive('😓 Tough but finished') ? 'bg-blue-500/20 text-blue-300 border-blue-400/40' : 'bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 border-gray-600/40'}`}>😓 Tough but finished</button>
                      <button type="button" onClick={() => toggleChip('💪 Crushed it!')} className={`px-3 py-2 text-xs rounded-lg border transition-colors active:scale-95 ${isChipActive('💪 Crushed it!') ? 'bg-blue-500/20 text-blue-300 border-blue-400/40' : 'bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 border-gray-600/40'}`}>💪 Crushed it!</button>
                      <button type="button" onClick={() => toggleChip("🤔 Could've been better")} className={`px-3 py-2 text-xs rounded-lg border transition-colors active:scale-95 ${isChipActive("🤔 Could've been better") ? 'bg-blue-500/20 text-blue-300 border-blue-400/40' : 'bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 border-gray-600/40'}`}>🤔 Could've been better</button>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={() => toggleChip('🤕 Not feeling well')} className={`px-3 py-2 text-xs rounded-lg border transition-colors active:scale-95 ${isChipActive('🤕 Not feeling well') ? 'bg-blue-500/20 text-blue-300 border-blue-400/40' : 'bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 border-gray-600/40'}`}>🤕 Not feeling well</button>
                      <button type="button" onClick={() => toggleChip('😴 Too tired / needed rest')} className={`px-3 py-2 text-xs rounded-lg border transition-colors active:scale-95 ${isChipActive('😴 Too tired / needed rest') ? 'bg-blue-500/20 text-blue-300 border-blue-400/40' : 'bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 border-gray-600/40'}`}>😴 Too tired / needed rest</button>
                      <button type="button" onClick={() => toggleChip('🌧️ Bad weather')} className={`px-3 py-2 text-xs rounded-lg border transition-colors active:scale-95 ${isChipActive('🌧️ Bad weather') ? 'bg-blue-500/20 text-blue-300 border-blue-400/40' : 'bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 border-gray-600/40'}`}>🌧️ Bad weather</button>
                      <button type="button" onClick={() => toggleChip('📅 Schedule conflict')} className={`px-3 py-2 text-xs rounded-lg border transition-colors active:scale-95 ${isChipActive('📅 Schedule conflict') ? 'bg-blue-500/20 text-blue-300 border-blue-400/40' : 'bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 border-gray-600/40'}`}>📅 Schedule conflict</button>
                      <button type="button" onClick={() => toggleChip('🎯 Intentional rest day')} className={`px-3 py-2 text-xs rounded-lg border transition-colors active:scale-95 ${isChipActive('🎯 Intentional rest day') ? 'bg-blue-500/20 text-blue-300 border-blue-400/40' : 'bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 border-gray-600/40'}`}>🎯 Intentional rest day</button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Custom Notes Textarea - Single line that auto-expands */}
              <div className="bg-gray-700/40 border border-gray-600/50 rounded-lg p-4 backdrop-blur-sm">
                <p className="text-xs text-gray-300 mb-2">Or write your own:</p>
                  <textarea
                    name="completion_details"
                    value={formData.completion_details}
                    onChange={(e) => {
                      handleChange(e);
                      // Auto-expand textarea
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                  placeholder={formData.status === 'completed' 
                    ? "Distance, time, how you felt, heart rate, any observations..." 
                    : "Tell me what happened (optional)..."}
                  className="w-full bg-transparent text-gray-200 placeholder-gray-400 border-none outline-none resize-none overflow-hidden"
                  rows={1}
                  disabled={loading}
                  style={{ minHeight: '24px' }}
                />
                <div className="mt-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-4 py-2.5 sm:py-2 bg-blue-500 text-white text-sm font-semibold rounded-lg hover:bg-blue-600 active:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:scale-[0.98]"
                  >
                    {loading ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </form>
            )}

            {/* Workout Details */}
            {currentWorkout?.details && (
              <div className="space-y-5">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-200 uppercase tracking-wide">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Details
                  {/* Enhancement Status Indicator */}
                  {isDetailsEnhanced(currentWorkout.details, currentWorkout.enhancement_status) ? (
                    currentWorkout.enhancement_status === "updated" ? (
                      <span 
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-400/30 rounded-full cursor-help"
                        title="Updated - This workout was updated by you and includes detailed coaching guidance"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Updated
                      </span>
                    ) : (
                      <span 
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-500/20 text-green-300 border border-green-400/30 rounded-full cursor-help"
                        title="Enhanced - This workout includes detailed coaching guidance, form cues, pacing strategies, and technical tips"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Enhanced
                      </span>
                    )
                  ) : (
                    <span 
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded-full cursor-help"
                      title="Core - This workout has the essential information. Detailed coaching content is being added in the background"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Core
                    </span>
                  )}
                </label>
                <div ref={workoutDetailsRef}>
                <ContentParser 
                  content={currentWorkout.details} 
                  workoutContext={workoutContext}
                />
                </div>
              </div>
            )}
          </div>
        
        {/* Minimized Chats within Workout Modal */}
        <MinimizedChatsInWorkout />
      </div>
    </div>,
    document.body
  );
};

const RegimenDayModal = ({ isOpen, onClose, run, type, onSuccess, regimens = [], onWorkoutChange }) => {
  return (
    <HelpModeProvider>
      <RegimenDayModalContent
        isOpen={isOpen}
        onClose={onClose}
        run={run}
        type={type}
        onSuccess={onSuccess}
        regimens={regimens}
        onWorkoutChange={onWorkoutChange}
      />
    </HelpModeProvider>
  );
};

export default RegimenDayModal; 